import React, { useState } from "react";
import {
  MapPin,
  Phone,
  Clock,
  Instagram,
  MessageCircle,
  Navigation,
  Mail,
  Send,
  Sparkles,
  CheckCircle2,
  UtensilsCrossed,
  User,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { businessInfo } from "../data/business";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setSubmitted(true);
    toast.success("Thank you! We received your message and will reach out shortly.");
  };

  return (
    <div className="space-y-12 sm:space-y-16 pb-16">
      {/* HEADER */}
      <section className="bg-gradient-to-b from-sky-50/70 to-transparent pt-8 sm:pt-14 pb-6 border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-sky-200 text-sky-800 text-xs sm:text-sm font-bold shadow-xs">
            <Sparkles className="w-4 h-4 text-sky-600" />
            <span>Visit Us in Udaipur</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#2C3E50] tracking-tight">
            Contact & Directions
          </h1>

          <p className="text-sm sm:text-base text-stone-600 max-w-xl mx-auto">
            Have a question, catering request, or want to say hello? Drop by our store in Udaipur or get in touch with us directly.
          </p>
        </div>
      </section>

      {/* CONTACT INFO & MAP SECTION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          
          {/* LEFT: CONTACT CARDS & ACTION BUTTONS (5 Cols) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-200/80 shadow-xs space-y-6">
              <h2 className="text-xl font-bold text-[#2C3E50]">
                Store Details
              </h2>

              <div className="space-y-4 text-sm text-stone-700">
                {/* Owner */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900">Owner</h3>
                    <p className="text-stone-600 mt-0.5 font-medium">{businessInfo.owner}</p>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900">Café Location</h3>
                    <p className="text-stone-600 mt-0.5">{businessInfo.address.full}</p>
                  </div>
                </div>

                {/* Timings */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900">Operating Hours</h3>
                    <p className="text-stone-600 mt-0.5">{businessInfo.timings.hours}</p>
                    <p className="text-xs text-stone-400">{businessInfo.timings.days}</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900">Call Us</h3>
                    <a
                      href={`tel:${businessInfo.contact.phone}`}
                      className="text-sky-600 font-semibold hover:underline mt-0.5 block"
                    >
                      {businessInfo.contact.phoneDisplay}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900">Email Inquiry</h3>
                    <a
                      href={`mailto:${businessInfo.contact.email}`}
                      className="text-stone-600 hover:text-sky-600 mt-0.5 block"
                    >
                      {businessInfo.contact.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="pt-4 border-t border-stone-100 grid grid-cols-2 gap-3">
                <a
                  href={businessInfo.contact.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs sm:text-sm py-2.5 px-3 rounded-xl shadow transition"
                >
                  <Navigation className="w-4 h-4" />
                  <span>Get Directions</span>
                </a>

                <a
                  href={`tel:${businessInfo.contact.phone}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#2C3E50] hover:bg-[#1e2b37] text-white font-bold text-xs sm:text-sm py-2.5 px-3 rounded-xl shadow transition"
                >
                  <Phone className="w-4 h-4" />
                  <span>Call Store</span>
                </a>

                <a
                  href={`https://wa.me/${businessInfo.contact.whatsapp.replace(/[^0-9]/g, "")}?text=Hi%20HimCream%20Naturals,%20I%20have%20an%20inquiry.`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold text-xs sm:text-sm py-2.5 px-3 rounded-xl shadow transition"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp</span>
                </a>

                <a
                  href={businessInfo.contact.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs sm:text-sm py-2.5 px-3 rounded-xl shadow transition"
                >
                  <Instagram className="w-4 h-4" />
                  <span>Instagram</span>
                </a>
              </div>
            </div>

            {/* Dine in notice */}
            <div className="p-5 rounded-3xl bg-gradient-to-br from-amber-50 to-sky-50 border border-sky-100 flex items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-sm text-[#2C3E50] flex items-center gap-1.5">
                  <UtensilsCrossed className="w-4 h-4 text-sky-600" />
                  <span>Visiting Our Café Right Now?</span>
                </h4>
                <p className="text-xs text-stone-500 mt-1">
                  You can order directly to your table with mobile QR.
                </p>
              </div>

              <Link
                to="/select-table"
                className="bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl whitespace-nowrap shadow transition"
              >
                Select Table
              </Link>
            </div>
          </div>

          {/* RIGHT: MAP EMBED & QUICK MESSAGE FORM (7 Cols) */}
          <div className="lg:col-span-7 space-y-6">
            {/* Interactive Map */}
            <div className="bg-white rounded-3xl overflow-hidden border border-stone-200/80 shadow-xs h-72 sm:h-80">
              <iframe
                title="HimCream Naturals Udaipur Location"
                src={businessInfo.contact.mapsEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Quick message form */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-200/80 shadow-xs">
              <h3 className="text-xl font-bold text-[#2C3E50] mb-1">
                Send Us a Message
              </h3>
              <p className="text-xs sm:text-sm text-stone-500 mb-5">
                Bulk ice cream tubs, event catering, or feedback — we'd love to hear from you.
              </p>

              {submitted ? (
                <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-2">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
                  <h4 className="font-bold text-emerald-800">Message Received!</h4>
                  <p className="text-xs text-emerald-700">
                    Thank you for reaching out to HimCream Naturals. Our team will contact you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-stone-700 mb-1">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Rahul Sharma"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-stone-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="e.g. 98290 12345"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-stone-700 mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Event Catering / Birthday Party Inquiry"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-stone-700 mb-1">
                      Message
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Tell us what flavours or details you need..."
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                    />
                  </div>

                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs sm:text-sm py-3 px-6 rounded-xl shadow transition w-full sm:w-auto"
                  >
                    <Send className="w-4 h-4" />
                    <span>Send Message</span>
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
